import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Animated,
  Platform,
  Dimensions,
} from 'react-native';
import LoadingAnimation from './LoadingAnimation';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { formatDistance, getMatchQualityLabel } from '../services/matchingService';

// Sample profile pictures to cycle through (placeholder URLs)
const SAMPLE_PROFILE_PICS = [
  'https://i.pravatar.cc/150?img=1',
  'https://i.pravatar.cc/150?img=2',
  'https://i.pravatar.cc/150?img=3',
  'https://i.pravatar.cc/150?img=4',
  'https://i.pravatar.cc/150?img=5',
  'https://i.pravatar.cc/150?img=6',
  'https://i.pravatar.cc/150?img=7',
  'https://i.pravatar.cc/150?img=8',
];

const CHECKER_PATTERN_IMAGE = {
  uri: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAwAAAAMCAYAAABWdVznAAAANElEQVQoU2NkYGBg+M8ABYwMjIwMDAwQGhAGqIDhRgxAjEGAwPDfzAwMDAwGiBqBFQGAGnfBtGp9MIeAAAAAElFTkSuQmCC',
};

const { width: windowWidth } = Dimensions.get('window');
const isDesktop = windowWidth >= 768;

const QuickPairSearchOverlay = ({
  visible,
  searching,
  matchFound,
  matchedUser,
  matchDistance,
  matchPhase,
  timeRemaining,
  myRating,
  myProfilePictureUrl,
  myUsername,
  onSendMessage,
  onCancel,
}) => {
  const [pulseAnim] = useState(new Animated.Value(1));
  const [cyclingSampleIndex, setCyclingSampleIndex] = useState(0);
  const [animateToMarker] = useState(new Animated.Value(0));
  const [checkerAnim] = useState(new Animated.Value(0));

  // Pulse animation for VS text during search
  useEffect(() => {
    if (searching && !matchFound) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(pulseAnim, {
            toValue: 1.2,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(pulseAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
        ])
      ).start();
    } else {
      pulseAnim.setValue(1);
    }
  }, [searching, matchFound]);

  // Checkerboard background animation
  useEffect(() => {
    if (visible) {
      const loop = Animated.loop(
        Animated.timing(checkerAnim, {
          toValue: 1,
          duration: 6000,
          useNativeDriver: true,
        })
      );
      loop.start();
      return () => loop.stop();
    }
    checkerAnim.setValue(0);
  }, [visible, checkerAnim]);

  // Cycle through sample profile pictures
  useEffect(() => {
    if (searching && !matchFound) {
      const interval = setInterval(() => {
        setCyclingSampleIndex((prev) => (prev + 1) % SAMPLE_PROFILE_PICS.length);
      }, 200); // Change every 200ms
      return () => clearInterval(interval);
    }
  }, [searching, matchFound]);

  // Animate to marker when match is found
  useEffect(() => {
    if (matchFound) {
      Animated.timing(animateToMarker, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }).start();
    } else {
      animateToMarker.setValue(0);
    }
  }, [matchFound]);

  if (!visible) return null;

  const checkerTranslate = checkerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 60],
  });

  const getProfilePicture = () => {
    if (matchFound && matchedUser?.profilePictureUrl) {
      return { uri: matchedUser.profilePictureUrl };
    }
    return null;
  };

  const getMyProfilePicture = () => {
    if (myProfilePictureUrl) {
      return { uri: myProfilePictureUrl };
    }
    return null;
  };

  return (
    <View style={[styles.overlay, !isDesktop && styles.overlayMobile]}>
      {/* Animated checkerboard background */}
      <Animated.Image
        source={CHECKER_PATTERN_IMAGE}
        resizeMode="repeat"
        style={[
          styles.checkerboard,
          {
            transform: [
              { translateX: Animated.multiply(checkerTranslate, 0.6) },
              { translateY: checkerTranslate },
            ],
          },
        ]}
        pointerEvents="none"
      />

      {/* Cancel Button */}
      <TouchableOpacity
        style={styles.cancelButton}
        onPress={onCancel}
        activeOpacity={0.8}
      >
        <Icon name="close" size={28} color="white" />
      </TouchableOpacity>

      {/* Content */}
      <View style={[styles.content, !isDesktop && styles.contentMobile]}>
        {searching && !matchFound ? (
          // Searching State
          <>
            <Text style={[styles.title, !isDesktop && styles.titleMobile]}>Finding opponent...</Text>
            
            <View style={[styles.belowTitleBlock, !isDesktop && styles.belowTitleBlockMobile]}>
              {/* Loading animation (same as splash screen) */}
              <View style={[styles.knightContainer, !isDesktop && styles.knightContainerMobile]}>
                <LoadingAnimation size={isDesktop ? 140 : 200} />
              </View>

              {/* Profile Circles */}
              <View style={[styles.profilesContainer, !isDesktop && styles.profilesContainerSearchingMobile]}>
              {/* My Profile */}
              <View style={[styles.profileCircle, isDesktop && styles.profileCircleDesktop]}>
                {getMyProfilePicture() ? (
                  <Image source={getMyProfilePicture()} style={[styles.profileImage, isDesktop && styles.profileImageDesktop]} />
                ) : (
                  <Icon name="person" size={isDesktop ? 64 : 50} color="#94EB98" />
                )}
              </View>

              {/* VS Text */}
              <Animated.View style={[styles.vsContainer, isDesktop && styles.vsContainerDesktop, { transform: [{ scale: pulseAnim }] }]}>
                <Text style={[styles.vsText, isDesktop && styles.vsTextDesktop]}>VS</Text>
              </Animated.View>

              {/* Unknown Opponent - Cycling Profile Pictures with Question Mark */}
              <View style={[styles.profileCircle, styles.unknownProfile, isDesktop && styles.profileCircleDesktop]}>
                <Image
                  source={{ uri: SAMPLE_PROFILE_PICS[cyclingSampleIndex] }}
                  style={[styles.profileImage, isDesktop && styles.profileImageDesktop]}
                />
                {/* Question Mark Overlay */}
                <View style={[styles.questionMarkOverlay, isDesktop && styles.questionMarkOverlayDesktop]}>
                  <Icon name="help-outline" size={isDesktop ? 72 : 60} color="rgba(255, 255, 255, 0.95)" />
                </View>
              </View>
            </View>

            {/* Timer */}
            <View style={[styles.timerContainer, !isDesktop && styles.timerContainerMobile]}>
              <Icon name="timer" size={20} color="white" />
              <Text style={styles.timerText}>
                {timeRemaining}s
              </Text>
            </View>

            <Text style={[styles.subtitle, !isDesktop && styles.subtitleMobile]}>
              Searching for players nearby...
            </Text>
          </View>
          </>
        ) : matchFound && matchedUser ? (
          // Match Found State - Beautiful side-by-side profile comparison
          <>
            <Text style={styles.title}>🎉 Match Found!</Text>
            
            {/* Match quality badge */}
            <View style={styles.qualityBadge}>
              <Icon
                name={matchPhase === 1 ? 'star' : matchPhase === 2 ? 'thumb-up' : 'check'}
                size={16}
                color="white"
              />
              <Text style={styles.qualityText}>{getMatchQualityLabel(matchPhase)}</Text>
            </View>

            {/* Side-by-side Profile Comparison */}
            <View style={styles.profilesContainer}>
              {/* My Profile Card */}
              <View style={[styles.profileCard, isDesktop && styles.profileCardDesktop]}>
                <View style={[styles.profileCircle, isDesktop && styles.profileCircleDesktop]}>
                  {getMyProfilePicture() ? (
                    <Image source={getMyProfilePicture()} style={[styles.profileImage, isDesktop && styles.profileImageDesktop]} />
                  ) : (
                    <Icon name="person" size={isDesktop ? 64 : 50} color="#94EB98" />
                  )}
                </View>
                <Text style={styles.profileName}>{myUsername || 'You'}</Text>
                <View style={styles.ratingBadge}>
                  <Icon name="emoji-events" size={14} color="#FFD700" />
                  <Text style={styles.ratingValue}>{myRating || 1200}</Text>
                </View>
              </View>

              {/* VS Divider */}
              <View style={styles.vsDivider}>
                <View style={styles.vsLine} />
                <View style={[styles.vsContainer, isDesktop && styles.vsContainerDesktop]}>
                  <Text style={[styles.vsText, isDesktop && styles.vsTextDesktop]}>VS</Text>
                </View>
                <View style={styles.vsLine} />
              </View>

              {/* Opponent Profile Card */}
              <View style={[styles.profileCard, isDesktop && styles.profileCardDesktop]}>
                <View style={[styles.profileCircle, styles.opponentCircle, isDesktop && styles.profileCircleDesktop]}>
                  {getProfilePicture() ? (
                    <Image source={getProfilePicture()} style={[styles.profileImage, isDesktop && styles.profileImageDesktop]} />
                  ) : (
                    <Icon name="person" size={isDesktop ? 64 : 50} color="#94EB98" />
                  )}
                </View>
                <Text style={styles.profileName}>{matchedUser.username}</Text>
                <View style={styles.ratingBadge}>
                  <Icon name="emoji-events" size={14} color="#FFD700" />
                  <Text style={styles.ratingValue}>{matchedUser.inPersonRating || 1200}</Text>
                </View>
              </View>
            </View>

            {/* Distance info */}
            <View style={styles.distanceInfo}>
              <Icon name="place" size={18} color="#94EB98" />
              <Text style={styles.distanceText}>{formatDistance(matchDistance)} away</Text>
            </View>

            {/* Send Message Button */}
            <TouchableOpacity
              style={styles.sendMessageButton}
              onPress={onSendMessage}
              activeOpacity={0.8}
            >
              <Icon name="chat" size={20} color="white" />
              <Text style={styles.sendMessageText}>Send Message</Text>
            </TouchableOpacity>

            {/* Cancel option */}
            <TouchableOpacity onPress={onCancel} style={styles.declineButton}>
              <Text style={styles.declineText}>Find someone else</Text>
            </TouchableOpacity>
          </>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 20000,
    elevation: 50,
    ...(Platform.OS === 'web' ? {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'auto',
    } : {}),
  },
  overlayMobile: {
    justifyContent: 'flex-start',
    paddingTop: 100,
  },
  checkerboard: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.12,
  },
  cancelButton: {
    position: 'absolute',
    top: Platform.OS === 'ios' ? 60 : 40,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  content: {
    alignItems: 'center',
    paddingHorizontal: 40,
  },
  contentMobile: {
    paddingTop: 0,
    paddingBottom: 110,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 12,
  },
  titleMobile: {
    fontSize: 22,
    marginBottom: 0,
  },
  belowTitleBlock: {},
  belowTitleBlockMobile: {
    marginTop: -36,
  },
  qualityBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    backgroundColor: '#94EB98',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginBottom: 24,
  },
  qualityText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
  knightContainer: {
    marginVertical: 20,
  },
  knightContainerMobile: {
    marginVertical: 0,
    marginTop: -8,
    marginBottom: -12,
  },
  profilesContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 16,
    marginVertical: 24,
  },
  profilesContainerSearchingMobile: {
    marginTop: -12,
    marginBottom: 8,
  },
  profileCard: {
    alignItems: 'center',
    width: 120,
  },
  profileCardDesktop: {
    width: 160,
  },
  profileCircle: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: 3,
    borderColor: '#94EB98',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  profileCircleDesktop: {
    width: 130,
    height: 130,
    borderRadius: 65,
  },
  opponentCircle: {
    borderColor: '#FFD700',
  },
  unknownProfile: {
    borderColor: '#666',
  },
  profileImage: {
    width: 92,
    height: 92,
    borderRadius: 46,
  },
  profileImageDesktop: {
    width: 120,
    height: 120,
    borderRadius: 60,
  },
  profileName: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
    marginTop: 12,
    textAlign: 'center',
  },
  ratingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
    marginTop: 6,
  },
  ratingValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: 'white',
  },
  questionMarkOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 55,
    justifyContent: 'center',
    alignItems: 'center',
  },
  questionMarkOverlayDesktop: {
    borderRadius: 65,
  },
  ratingText: {
    position: 'absolute',
    bottom: -20,
    fontSize: 16,
    fontWeight: 'bold',
    color: 'white',
    backgroundColor: '#2E7D32',
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  vsDivider: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
  },
  vsLine: {
    width: 2,
    height: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  vsContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 8,
  },
  vsContainerDesktop: {
    width: 56,
    height: 56,
    borderRadius: 28,
  },
  vsText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: 'white',
  },
  vsTextDesktop: {
    fontSize: 20,
  },
  distanceInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 24,
  },
  distanceText: {
    fontSize: 16,
    color: '#94EB98',
    fontWeight: '500',
  },
  sendMessageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: '#84D288',
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 25,
    shadowColor: '#7FB690',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 5,
    minWidth: 220,
  },
  sendMessageText: {
    fontSize: 18,
    fontWeight: '600',
    color: 'white',
  },
  timerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    marginTop: 16,
  },
  timerContainerMobile: {
    marginTop: 6,
  },
  timerText: {
    fontSize: 16,
    fontWeight: '600',
    color: 'white',
  },
  loader: {
    marginVertical: 24,
  },
  subtitle: {
    fontSize: 16,
    color: '#ccc',
    textAlign: 'center',
    marginTop: 16,
  },
  subtitleMobile: {
    marginTop: 8,
    marginBottom: 16,
  },
  declineButton: {
    marginTop: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 25,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
  declineText: {
    fontSize: 14,
    fontWeight: '600',
    color: 'white',
  },
});

export default QuickPairSearchOverlay;
